// ── InAppBrowserBanner ───────────────────────────────────────────────────────
// Shown when Clarity detects it's running inside Instagram/Facebook/TikTok's
// built-in link browser (see useInAppBrowser.js for why that matters — those
// browsers can close themselves and dump the user back into the host app when
// a native time/date picker opens, which otherwise reads as "the app randomly
// closes"). Dismissal is session-only (plain useState in the parent, not
// persisted) — if the user is still in the in-app browser next visit, the bug
// is still there, so the nudge should still show.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";

// Fixed-position (so it's never scrolled away), but that means it's removed
// from document flow and would sit ON TOP of the app's own fixed/sticky
// headers (e.g. the Planner's sticky month/week bar) rather than pushing them
// down. Publishing the banner's real rendered height — which varies with text
// wrap across screen widths — as a CSS var lets the root layout (App.jsx) add
// matching top padding, so everything else shifts down instead of colliding.
function useBannerHeightVar(active) {
  const ref = useRef(null);

  useEffect(() => {
    if (!active) {
      document.documentElement.style.removeProperty("--in-app-banner-h");
      return;
    }
    const el = ref.current;
    if (!el) return;
    const publish = () => document.documentElement.style.setProperty("--in-app-banner-h", `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--in-app-banner-h");
    };
  }, [active]);

  return ref;
}

export default function InAppBrowserBanner({ browserName, onDismiss }) {
  const [copying, setCopying] = useState(false);
  const bannerRef = useBannerHeightVar(true);

  const handleCopyLink = async () => {
    setCopying(true);
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied. Paste it in Chrome or Safari");
    } catch {
      toast.error("Could not copy the link. Try selecting the address bar text instead.");
    } finally {
      setCopying(false);
    }
  };

  return (
    <div ref={bannerRef} style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 200, paddingTop: "var(--sat, 0px)",
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", background: "var(--c-bg-soft, #fff5f9)",
      borderBottom: "1.5px solid var(--c-border-light, #f0d8ea)",
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      fontFamily: "'Quicksand', sans-serif",
    }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>🌸</span>
      <div style={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.4, color: "var(--c-text, #2a2436)" }}>
        You're viewing Clarity inside {browserName}'s browser. Some things (like tapping a time field) may close it unexpectedly.{" "}
        <button
          type="button"
          onClick={handleCopyLink}
          disabled={copying}
          style={{
            background: "none", border: "none", padding: 0, font: "inherit",
            fontWeight: 800, color: "var(--c-text-accent, #c2447a)",
            textDecoration: "underline", cursor: copying ? "default" : "pointer",
          }}
        >
          Copy link to open in your regular browser
        </button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          flexShrink: 0, background: "none", border: "none", cursor: "pointer",
          color: "var(--c-text-soft, #6b6478)", padding: 4, display: "flex",
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
